import { useRef, useState } from "react";
import styled from "styled-components";
import {
  ButtonLink,
  ButtonRound,
  DescriptionList,
  DrawerPanel,
  ExportIcon,
  Notice,
  Presence,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import { DomainStatus, escapeHtml, formatBRL, formatCompetencia, formatDate } from "@components/ow";
import { INVOICE_STATUS, Inline, Stack } from "../PaymentSchedules";

// Notas fiscais, no que a visão da empresa (/c/nfes) e a do prestador (/e/nfes)
// compartilham: pílulas de status, células da lista e o drawer da nota.

// Pílulas do TableFilters: uma por vez, "Todos" sem filtro
export const INVOICE_STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Pendente", value: "PENDING" },
  { label: "Aprovada", value: "APPROVED" },
  { label: "Paga", value: "PAID" },
  { label: "Recusada", value: "REJECTED" },
];

export const invoiceCompetenceOf = (nf) => ({ month: Number(nf.month), year: Number(nf.year) });

// Os modais do cronograma usam o cronograma só para a competência no texto
export const competenceSchedule = (nf) => ({ reference_month: nf.month, reference_year: nf.year });

export const stopRowClick = (event) => event.stopPropagation();

// O ButtonLink da lib não quebra linha: nome de arquivo longo corta com reticências
export const FileName = styled.span`
  display: block;
  max-width: 100%;
  min-width: 0;

  button {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
  }
`;

// Motivo da recusa na linha: até 2 linhas, o texto inteiro fica no drawer
const ReasonLines = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  max-width: 260px;
`;

/** Nome do arquivo que baixa a nota; na linha da tabela, não abre o drawer. */
export function InvoiceFileLink({ invoice, onDownload }) {
  return (
    <FileName onClick={stopRowClick}>
      <ButtonLink title={invoice.filename} $text={invoice.filename || "Nota fiscal"} $action={onDownload} $noPadding $noWidth />
    </FileName>
  );
}

/** Célula do status: a pílula, "Paga em …" e, com `showReason`, o motivo da recusa. */
export function InvoiceStatusCell({ invoice, showReason }) {
  const reason = showReason && invoice.status === "REJECTED" ? invoice.rejection_reason : null;
  return (
    <Stack $gap="2px" $align="flex-start">
      <DomainStatus domain="notaFiscal" value={INVOICE_STATUS[invoice.status] || invoice.status} />
      {invoice.status === "PAID" && invoice.paid_at ? (
        <Text $text={`Paga em ${formatDate(invoice.paid_at)}`} $variant="caption_medium" $color="N400" />
      ) : null}
      {reason ? (
        <ReasonLines title={reason}>
          <Text $text="Motivo: %s" $replaces={[escapeHtml(reason)]} $variant="caption_medium" $color="N400" />
        </ReasonLines>
      ) : null}
    </Stack>
  );
}

/**
 * Drawer da nota e o cronograma dela ("Ver pagamento"). `fetchSchedule(nf)` devolve
 * o cronograma da nota; sem `enabled` (sem a tela de pagamentos), não busca.
 * A nota recusada não tem cronograma: o back desfaz o vínculo.
 */
export function useInvoiceDrawer({ enabled, fetchSchedule }) {
  const [open, setOpen] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [scheduleId, setScheduleId] = useState(null);
  const lastDetail = useRef(0);

  async function openDrawer(nf) {
    const request = ++lastDetail.current;
    setInvoice(nf);
    setScheduleId(null);
    setOpen(true);
    if (!enabled || nf.status === "REJECTED") return;
    try {
      const schedule = await fetchSchedule(nf);
      if (request === lastDetail.current) setScheduleId(schedule?.id || null);
    } catch (error) {
      // Sem o cronograma, o drawer só fica sem o "Ver pagamento"
    }
  }

  return { open, invoice, scheduleId, openDrawer, close: () => setOpen(false) };
}

/** Conta com o dígito, como o export de pagamentos. */
function bankAccount(bank) {
  if (!bank?.bank_account) return "-";
  return bank.bank_account_digit ? `${bank.bank_account}-${bank.bank_account_digit}` : bank.bank_account;
}

function bankName(bank) {
  if (!bank?.bank_name) return "-";
  return bank.bank_code ? `${bank.bank_name} (${bank.bank_code})` : bank.bank_name;
}

/**
 * Drawer com os dados da nota. `subtitle` vem escapado de quem chama (o prestador na
 * visão da empresa); `showBankData` mostra o bloco quando o back manda o `bankData`;
 * `onOpenPayment` aparece como "Ver pagamento".
 */
export function InvoiceDrawer({ drawer, subtitle, showBankData, onDownload, onOpenPayment }) {
  const nf = drawer.invoice;
  const bank = nf?.bankData;
  const withBank = showBankData && nf && "bankData" in nf;

  return (
    <Presence show={drawer.open}>
      {nf ? (
        <DrawerPanel $showDrawer={drawer.open} $setShowDrawer={drawer.close} $direction="right" $size="panel">
          <Stack $gap="24px">
            <Stack $gap="4px">
              <Text $text={`Nota de ${formatCompetencia(invoiceCompetenceOf(nf), true)}`} $variant="h4_bold" $color="N900" />
              {subtitle ? <Text $text={subtitle} $variant="caption_medium" $color="N400" /> : null}
            </Stack>

            <Stack $gap="16px">
              <DescriptionList
                $title="Nota fiscal"
                $columns={1}
                $items={[
                  {
                    label: "Arquivo",
                    value: (
                      <Inline>
                        <FileName>
                          <ButtonLink
                            title={nf.filename}
                            $text={nf.filename || "Nota fiscal"}
                            $action={() => onDownload(nf)}
                            $noPadding
                            $noWidth
                          />
                        </FileName>
                        <DomainStatus domain="notaFiscal" value={INVOICE_STATUS[nf.status] || nf.status} />
                        <ButtonRound
                          title="Baixar nota fiscal"
                          aria-label="Baixar nota fiscal"
                          $variant="outline"
                          $icon={<ExportIcon />}
                          $noWidth
                          $action={() => onDownload(nf)}
                        />
                      </Inline>
                    ),
                  },
                  { label: "Valor da nota", value: formatBRL(nf.value) },
                  { label: "Enviada em", value: formatDate(nf.created_at) },
                  ...(nf.status === "PAID" ? [{ label: "Paga em", value: formatDate(nf.paid_at) }] : []),
                ]}
              />
              {nf.status === "REJECTED" && nf.rejection_reason ? (
                <Notice $variant="red" $title="Motivo da recusa" $text="%s" $replaces={[escapeHtml(nf.rejection_reason)]} />
              ) : null}
            </Stack>

            {withBank && !bank ? (
              <Notice $variant="gray" $title="Dados bancários" $text="O prestador ainda não cadastrou os dados bancários." />
            ) : null}
            {withBank && bank ? (
              <DescriptionList
                $title="Dados bancários"
                $columns={1}
                $items={[
                  { label: "Chave Pix", value: bank?.pix_key || "-" },
                  { label: "Banco", value: bankName(bank) },
                  { label: "Agência", value: bank?.bank_agency || "-" },
                  { label: "Conta", value: bankAccount(bank) },
                  { label: "Tipo de conta", value: bank?.bank_account_type || "-" },
                ]}
              />
            ) : null}

            {drawer.scheduleId && onOpenPayment ? (
              <div>
                <ButtonLink $text="Ver pagamento" $action={() => onOpenPayment(drawer.scheduleId)} $noPadding $noWidth />
              </div>
            ) : null}
          </Stack>
        </DrawerPanel>
      ) : null}
    </Presence>
  );
}

export { AwaitingInvoiceNotices, useAwaitingInvoices } from "./AwaitingInvoices";
